import { mapGetters } from 'vuex';
import { DateTime } from 'luxon';

export const FormatTimezone = {
    DATE_TIME: 'yyyy-MM-dd HH:mm:ss',
    DATE_TIME_WITHOUT_SEC: 'yyyy-MM-dd HH:mm',
    ONLY_DATE: 'yyyy-MM-dd',
    ONLY_TIME: 'HH:mm',
    ISO_FORMAT: 'yyyy-MM-dd\'T\'HH:mm:ssZZ',
};

export const ViewPeriodEnum = {
    GLOBAL: 'global',
    DAY: 'agendaDay',
    WEEK: 'agendaWeek',
    MONTH: 'month',
    QUARTER: 'quarter',
};

export const timezoneUtc = {
    computed: {
        ...mapGetters({
            getTimezone: 'getTimezone',
            getLocale: 'locale',
        }),
    },
    methods: {
        formatDateTimeToDateStringTitle,
        createDateTimeAsUTC,
        /**
         * @param { DateTime } nonUtcDate
         */
        convertDateTimeToUTC,
        /**
         * @param { string } dateStr
         */
        parseToDateTimeAsBrowserTimezone(dateStr) {
            const options = {
                timezone: DateTime.local().zone,
                local: this.getLocale,
            };
            return parseDateTimeString(dateStr, options);
        },
        /**
         * @param { string } dateStr
         */
        parseToDateTimeAsApplicationTimezone(dateStr) {
            const options = {
                timezone: this.getTimezone,
                local: this.getLocale,
            };
            return parseDateTimeString(dateStr, options);
        },
        /**
         * @param { string } dateStr
         */
        parseToDateTimeAsUTCTimezone(dateStr) {
            return parseToDateTimeAsUTCTimezoneWithLocal(dateStr, this.getLocale);
        },
        parseIsoDateTimeString,
        convertDateTimeToApplicationTimezone,
        formatDateTimeToString,
        getStartOfPeriodForDateTime,
        getEndOfPeriodForDateTime,
        isSamePeriodForDateTime,
        subFromDateTime,
        addToDateTime,
        formatDatesToUtcAndFormat,
        convertIsoDateToApplicationTimezone(dateStr, formatTo = '') {
            try {
                if (!dateStr) return '';
                const date = this.convertDateTimeToApplicationTimezone(
                    this.parseIsoDateTimeString(
                        dateStr
                    )
                );
                if (date.invalid) {
                    return '';
                }
                return formatTo ? date.toFormat(formatTo, { locale: this.getLocale }) : date;
            }
            catch (e) {
                return '';
            }
        },
        getCurrentDateString,
    },
};

export function getCurrentDateString() {
    return this.formatDateTimeToString(this.createDateTimeAsUTC(), FormatTimezone.ONLY_DATE);
}

export function parseToDateTimeAsUTCTimezoneWithLocal(dateStr, local = 'locale') {
    const options = {
        timezone: 'UTC',
        local,
    };
    return parseOnlyDateString(dateStr, options);
}

/**
 * @return DateTime
 */
export function createDateTimeAsUTC() {
    return DateTime.utc();
}

/**
 * @param { DateTime } nonUtcDate
*/
export function convertDateTimeToUTC(nonUtcDate) {
    return nonUtcDate.toUTC();
}
/**
 * @param { DateTime } date
 * @return DateTime
 */
export function convertDateTimeToApplicationTimezone(date) {
    return date.setZone(this.getTimezone).setLocale(this.getLocale);
}
/**
 * @param { string } dateStr
 * @return DateTime | string
 */
export function parseIsoDateTimeString(dateStr) {
    if (!dateStr) {
        return '';
    }
    return DateTime.fromFormat(dateStr, FormatTimezone.ISO_FORMAT);
}

/**
 * @param { string } dateStr
 * @param options
 * @param { string | Zone } options.timezone
 * @param { string } options.local
 * @return DateTime | string
 */
export function parseDateTimeString(dateStr, options) {
    if (!dateStr) {
        return '';
    }
    return DateTime.fromFormat(dateStr, FormatTimezone.DATE_TIME, { zone: options.timezone, locale: options.local });
}

/**
 * @param { string } dateStr
 * @param options
 * @param { string | Zone } options.timezone
 * @param { string } options.local
 * @return DateTime | string
 */
export function parseOnlyDateString(dateStr, options) {
    if (!dateStr) {
        return '';
    }
    return DateTime.fromFormat(dateStr, FormatTimezone.ONLY_DATE, { zone: options.timezone, locale: options.local });
}

/**
 * @return string
 * @param { DateTime } date
 * @param { string } formatStr
 */
export function formatDateTimeToString(date, formatStr) {
    return date.toFormat(formatStr);
}

/**
 * @return DateTime
 * @param { string } period
 * @param { DateTime } date
 */
export function getStartOfPeriodForDateTime(period, date) {
    switch (period) {
        case ViewPeriodEnum.DAY:
            return date.startOf('day');
        case ViewPeriodEnum.WEEK:
            return date.startOf('week');
        case ViewPeriodEnum.MONTH:
            return date.startOf('month');
        case ViewPeriodEnum.QUARTER:
            return date.startOf('month');
        default:
            return null;
    }
}

/**
 * @return DateTime
 * @param { string } period
 * @param { DateTime } date
 */
export function getEndOfPeriodForDateTime(period, date) {
    switch (period) {
        case ViewPeriodEnum.DAY:
            return date.endOf('day');
        case ViewPeriodEnum.WEEK:
            return date.endOf('week');
        case ViewPeriodEnum.MONTH:
            return date.endOf('month');
        case ViewPeriodEnum.QUARTER:
            const startMonth = date.startOf('month');
            return startMonth.plus({ month: 3 }).minus({ day: 1 });
        default:
            return null;
    }
}
/**
 * @return boolean
 * @param { string } period
 * @param { DateTime } date
 */
export function isSamePeriodForDateTime(period, date) {
    const currentDate = createDateTimeAsUTC();
    switch (period) {
        case ViewPeriodEnum.DAY:
            return date.hasSame(currentDate, 'day');
        case ViewPeriodEnum.WEEK:
            return date.hasSame(currentDate, 'week');
        case ViewPeriodEnum.MONTH:
            return date.hasSame(currentDate, 'month');
        case ViewPeriodEnum.QUARTER:
            return date.hasSame(currentDate, 'quarter');
        default:
            return false;
    }
}

/**
 * @return DateTime
 * @param { string } period
 * @param { DateTime } date
 */
export function subFromDateTime(period, date) {
    switch (period) {
        case ViewPeriodEnum.DAY:
            return date.minus({ day: 1 });
        case ViewPeriodEnum.WEEK:
            return date.minus({ week: 1 });
        case ViewPeriodEnum.MONTH:
            return date.minus({ month: 1 });
        case ViewPeriodEnum.QUARTER:
            return date.minus({ months: 3 });
        default:
            return null;
    }
}

/**
 * @return DateTime
 * @param { string } period
 * @param { DateTime } date
 */
export function addToDateTime(period, date) {
    switch (period) {
        case ViewPeriodEnum.DAY:
            return date.plus({ day: 1 });
        case ViewPeriodEnum.WEEK:
            return date.plus({ week: 1 });
        case ViewPeriodEnum.MONTH:
            return date.plus({ month: 1 });
        case ViewPeriodEnum.QUARTER:
            return date.plus({ months: 3 });
        default:
            return null;
    }
}

/**
 * @param params
 * @param { string } params.fromFormat
 * @param { string | undefined } params.toFormat
 * @param { string } params.dateStr
 * @param { DateTime.options } params.options
 * @return string
 */
export function formatDatesToUtcAndFormat(params) {
    return DateTime
        .fromFormat(
            params.dateStr,
            params.fromFormat,
            params.options)
        .toUTC()
        .toFormat(params.toFormat || params.fromFormat);
}

export function formatDateTimeToDateStringTitle(datetime) {
    return datetime.toLocaleString(DateTime.DATE_FULL);
}
